import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "../components/ui/dialog";
import { toast } from "sonner";
import { Plus, Trash2, Package, Search, X, AlertTriangle, Edit3, Minus, Printer, ScanLine } from "lucide-react";

const empty = { name: "", sku: "", category: "", compatible_models: "", price: 0, cost: 0, stock: 0, low_stock_threshold: 2, notes: "" };

export default function AdminParts() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [search, setSearch] = useState("");
  const [form, setForm] = useState(empty);

  const load = async () => { const r = await api.get("/admin/parts"); setItems(r.data); };
  useEffect(() => { load(); }, []);

  const startEdit = (p) => { setEditing(p); setForm({ ...empty, ...p }); setOpen(true); };
  const startNew = () => { setEditing(null); setForm(empty); setOpen(true); };

  const save = async (e) => {
    e.preventDefault();
    if (!form.name) { toast.error("Numele este obligatoriu"); return; }
    try {
      const body = { ...form, price: parseFloat(form.price) || 0, cost: parseFloat(form.cost) || 0, stock: parseInt(form.stock) || 0, low_stock_threshold: parseInt(form.low_stock_threshold) || 2 };
      if (editing) await api.patch(`/admin/parts/${editing.id}`, body);
      else await api.post("/admin/parts", body);
      toast.success(editing ? "Piesa actualizata" : "Piesa adaugata");
      setOpen(false); setForm(empty); setEditing(null);
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Eroare"); }
  };

  const adjustStock = async (p, delta) => {
    const newStock = Math.max(0, (p.stock || 0) + delta);
    await api.patch(`/admin/parts/${p.id}`, { stock: newStock });
    load();
  };

  const remove = async (id) => { if (!confirm("Sterge piesa?")) return; await api.delete(`/admin/parts/${id}`); load(); };

  const filtered = items.filter((p) => {
    if (!search.trim()) return true;
    const q = search.toLowerCase();
    return (p.name + " " + (p.sku||"") + " " + (p.category||"") + " " + (p.compatible_models||"")).toLowerCase().includes(q);
  });

  const lowStock = items.filter((p) => (p.stock || 0) <= (p.low_stock_threshold || 0));
  const totalValue = items.reduce((s, p) => s + (p.stock || 0) * (p.cost || 0), 0);

  return (
    <div data-testid="admin-parts-page">
      <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
        <div>
          <h1 className="font-display text-4xl uppercase">Piese & stoc</h1>
          <p className="text-sm text-neutral-400 mt-1">Gestiune piese si materiale</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Link to="/admin/scan" data-testid="goto-scan-mode" className="inline-flex items-center gap-2 border border-[#00E5FF] text-[#00E5FF] hover:bg-[#00E5FF] hover:text-black font-semibold uppercase tracking-wider px-5 py-3 text-sm transition-colors">
            <ScanLine className="w-4 h-4" /> Scan mode
          </Link>
          <Button onClick={startNew} data-testid="add-part-btn" className="bg-[#00E5FF] hover:bg-[#5CEEFF] text-black font-semibold uppercase tracking-wider rounded-none">
            <Plus className="w-4 h-4 mr-2" /> Piesa noua
          </Button>
        </div>
      </div>

      <div className="grid sm:grid-cols-3 gap-4 mb-6">
        <StatBox label="Piese in catalog" value={items.length} icon={Package} />
        <StatBox label="Valoare stoc (cost)" value={`${totalValue.toFixed(0)} RON`} highlight icon={Package} />
        <StatBox label="Stoc scazut" value={lowStock.length} icon={AlertTriangle} warn={lowStock.length > 0} />
      </div>

      <div className="mb-5 relative">
        <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-neutral-500" />
        <Input data-testid="part-search" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Cauta piesa, cod, model compatibil..." className="bg-neutral-900 border-neutral-800 rounded-none pl-10 pr-10 h-11 focus:border-[#00E5FF]" />
        {search && <button onClick={() => setSearch("")} className="absolute right-3 top-1/2 -translate-y-1/2 text-neutral-500 hover:text-[#00E5FF]"><X className="w-4 h-4" /></button>}
      </div>

      <div className="bg-neutral-950 border border-neutral-800 overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-neutral-900 border-b border-neutral-800">
            <tr className="text-left text-xs uppercase tracking-widest text-neutral-400">
              <th className="p-3">Piesa</th>
              <th className="p-3 hidden md:table-cell">Categorie / Compat.</th>
              <th className="p-3">Stoc</th>
              <th className="p-3 hidden sm:table-cell">Pret</th>
              <th className="p-3"></th>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 && <tr><td colSpan="5" className="p-8 text-center text-neutral-500">{items.length === 0 ? "Niciun produs inca. Adauga prima piesa." : "Niciun rezultat"}</td></tr>}
            {filtered.map((p) => {
              const low = (p.stock || 0) <= (p.low_stock_threshold || 0);
              return (
                <tr key={p.id} className="border-b border-neutral-900 hover:bg-neutral-900/40">
                  <td className="p-3">
                    <div className="font-medium">{p.name}</div>
                    {p.sku && <div className="text-[10px] text-neutral-500 font-mono uppercase mt-0.5">{p.sku}</div>}
                  </td>
                  <td className="p-3 hidden md:table-cell text-xs">
                    {p.category && <div className="text-neutral-300">{p.category}</div>}
                    {p.compatible_models && <div className="text-neutral-500">{p.compatible_models}</div>}
                  </td>
                  <td className="p-3">
                    <div className="flex items-center gap-1.5">
                      <button onClick={() => adjustStock(p, -1)} className="w-6 h-6 border border-neutral-700 hover:border-[#00E5FF] flex items-center justify-center text-neutral-400 hover:text-[#00E5FF]"><Minus className="w-3 h-3" /></button>
                      <span className={`font-display text-xl px-2 ${low ? "text-red-400" : "text-white"}`}>{p.stock || 0}</span>
                      <button onClick={() => adjustStock(p, 1)} className="w-6 h-6 border border-neutral-700 hover:border-[#00E5FF] flex items-center justify-center text-neutral-400 hover:text-[#00E5FF]"><Plus className="w-3 h-3" /></button>
                    </div>
                    {low && <div className="text-[9px] uppercase tracking-widest text-red-400 mt-1 flex items-center gap-1"><AlertTriangle className="w-2.5 h-2.5" /> Sub prag {p.low_stock_threshold}</div>}
                  </td>
                  <td className="p-3 hidden sm:table-cell">
                    <div className="text-[#00E5FF] font-semibold">{p.price} RON</div>
                    {p.cost > 0 && <div className="text-[10px] text-neutral-500">cost: {p.cost}</div>}
                  </td>
                  <td className="p-3">
                    <div className="flex gap-2 justify-end">
                      <button onClick={() => window.open(`/admin/piese/${p.id}/barcode`, "_blank")} className="text-neutral-400 hover:text-[#00E5FF]" title="Printeaza eticheta cu cod de bare">
                        <Printer className="w-4 h-4" />
                      </button>
                      <button onClick={() => startEdit(p)} className="text-neutral-400 hover:text-[#00E5FF]"><Edit3 className="w-4 h-4" /></button>
                      <button onClick={() => remove(p.id)} className="text-neutral-500 hover:text-red-500"><Trash2 className="w-4 h-4" /></button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-neutral-950 border-neutral-800 text-white max-w-2xl">
          <DialogHeader><DialogTitle className="font-display uppercase">{editing ? "Editeaza piesa" : "Piesa noua"}</DialogTitle></DialogHeader>
          <form onSubmit={save} className="space-y-4">
            <div className="grid sm:grid-cols-2 gap-4">
              <Field label="Nume *"><Input value={form.name} onChange={(e) => setForm({...form, name: e.target.value})} className="bg-neutral-900 border-neutral-800 rounded-none" /></Field>
              <Field label="Cod SKU (lasa gol = auto-generat)"><Input placeholder="ex: KK-ABC12345 (sau scaneaza codul aici)" value={form.sku || ""} onChange={(e) => setForm({...form, sku: e.target.value})} className="bg-neutral-900 border-neutral-800 rounded-none font-mono" /></Field>
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              <Field label="Categorie"><Input placeholder="ex: Baterie / Motor / Frana" value={form.category || ""} onChange={(e) => setForm({...form, category: e.target.value})} className="bg-neutral-900 border-neutral-800 rounded-none" /></Field>
              <Field label="Modele compatibile"><Input placeholder="ex: G2 Pro, G2 Max" value={form.compatible_models || ""} onChange={(e) => setForm({...form, compatible_models: e.target.value})} className="bg-neutral-900 border-neutral-800 rounded-none" /></Field>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
              <Field label="Pret (RON)"><Input type="number" step="0.01" value={form.price} onChange={(e) => setForm({...form, price: e.target.value})} className="bg-neutral-900 border-neutral-800 rounded-none" /></Field>
              <Field label="Cost (RON)"><Input type="number" step="0.01" value={form.cost} onChange={(e) => setForm({...form, cost: e.target.value})} className="bg-neutral-900 border-neutral-800 rounded-none" /></Field>
              <Field label="Stoc"><Input type="number" value={form.stock} onChange={(e) => setForm({...form, stock: e.target.value})} className="bg-neutral-900 border-neutral-800 rounded-none" /></Field>
              <Field label="Prag alerta"><Input type="number" value={form.low_stock_threshold} onChange={(e) => setForm({...form, low_stock_threshold: e.target.value})} className="bg-neutral-900 border-neutral-800 rounded-none" /></Field>
            </div>
            <Field label="Note"><Textarea rows={2} value={form.notes || ""} onChange={(e) => setForm({...form, notes: e.target.value})} className="bg-neutral-900 border-neutral-800 rounded-none" /></Field>
            <DialogFooter>
              <Button type="submit" className="bg-[#00E5FF] hover:bg-[#5CEEFF] text-black rounded-none">{editing ? "Salveaza" : "Adauga"}</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}

const Field = ({ label, children }) => (
  <div className="space-y-2">
    <Label className="text-xs uppercase tracking-wider text-neutral-400">{label}</Label>
    {children}
  </div>
);

const StatBox = ({ label, value, highlight, warn, icon: Icon }) => (
  <div className={`bg-neutral-950 border p-5 ${warn ? "border-red-500/50" : "border-neutral-800"}`}>
    <Icon className={`w-6 h-6 mb-3 ${warn ? "text-red-400" : highlight ? "text-[#00E5FF]" : "text-neutral-400"}`} />
    <div className={`font-display text-4xl ${warn ? "text-red-400" : highlight ? "text-[#00E5FF]" : "text-white"}`}>{value}</div>
    <div className="text-xs uppercase tracking-widest text-neutral-500 mt-1">{label}</div>
  </div>
);
